import { lazy, type ReactNode } from 'react';
import { Navigate, Outlet, type RouteObject } from 'react-router-dom';

import { RequireFlag, useEntitlements, type Capability } from '@core/access';
import { can } from '@core/access';
import { Loading } from '@ui/Loading';
import { RemountOn } from '@shared/components/remount-on/RemountOn';

/**
 * The assignment routes, and the gate in front of them.
 *
 * One subtree under `/assignments`, because a valuation request and the work
 * around it are one record: the list is the queue, `:id` is the record, and
 * the tasks, files, inspection and discussion are tabs on it rather than four
 * sibling screens that each re-derive which assignment they are looking at.
 *
 * `CapabilityGuard` is a convenience, not a security boundary: it decides what
 * to render, while the API applies its own policy to every request and would
 * refuse a caller who reached an endpoint by hand. What the guard buys is the
 * right failure - a 403 rather than a screen of empty grids.
 */

const AssignmentsPage = lazy(() => import('./pages/AssignmentsPage'));
const AssignmentRecordPage = lazy(() => import('./pages/AssignmentRecordPage'));
const NewAssignmentPage = lazy(() => import('./pages/NewAssignmentPage'));
const MasterDataPage = lazy(() => import('./pages/admin/MasterDataPage'));
/* Assist: the valuer's day, answers across the records, and bulk batches. */
const TodayPage = lazy(() => import('./assist/pages/TodayPage'));
const AskPage = lazy(() => import('./assist/pages/AskPage'));
const BatchPage = lazy(() => import('./assist/pages/BatchPage'));
/* Every job by lifecycle stage, opportunities included. */
const PipelinePage = lazy(() => import('./pages/PipelinePage'));

/*
 * The cross-record queues.
 *
 * "Every task assigned to me" and "every file across my jobs" are questions
 * about a person's week, not about one record, so they stay whole-screen while
 * the same panes render scoped inside a record's tabs.
 */
const AssignmentTasksPane = lazy(() => import('@features/my-assignments/pages/AssignmentTasksPane'));
const AssignmentFilesPage = lazy(() => import('@features/my-assignments/pages/AssignmentFilesPage'));

function CapabilityGuard({ capability, children }: { capability: Capability; children: ReactNode }) {
  const entitlements = useEntitlements();

  /*
   * Sign-in has not resolved yet.
   *
   * Redirecting on the empty grant that is there before the claims arrive
   * would bounce every user straight to 404 on a cold page load - the trap the
   * Ratings screens fell into, and the one the region gate fell into after
   * them.
   */
  if (entitlements.pending) {
    return <Loading variant="page" label="Checking access" />;
  }

  if (!can(entitlements, capability)) {
    return <Navigate to="/404" replace />;
  }

  return <>{children}</>;
}

export const valuationRoutes: RouteObject[] = [
  {
    path: 'pipeline',
    element: (
      <RequireFlag flag="pipelineBoard">
        <CapabilityGuard capability="assignments.view">
          <PipelinePage />
        </CapabilityGuard>
      </RequireFlag>
    )
  },
  {
    path: 'assignments',
    element: (
      <CapabilityGuard capability="assignments.view">
        <Outlet />
      </CapabilityGuard>
    ),
    children: [
      { index: true, element: <AssignmentsPage /> },
      {
        path: 'today',
        element: (
          <CapabilityGuard capability="assignments.assist">
            <TodayPage />
          </CapabilityGuard>
        )
      },
      {
        path: 'ask',
        element: (
          <CapabilityGuard capability="assignments.assist">
            <AskPage />
          </CapabilityGuard>
        )
      },
      {
        path: 'batches',
        element: (
          <CapabilityGuard capability="assignments.bulk">
            <BatchPage />
          </CapabilityGuard>
        )
      },
      { path: 'tasks', element: <AssignmentTasksPane /> },
      { path: 'files', element: <AssignmentFilesPage /> },
      // `new` and `admin` before the catch-all `:id`, so /assignments/new is
      // the (currently empty) create page rather than a lookup for a record called "new".
      {
        path: 'new',
        element: (
          <CapabilityGuard capability="assignments.create">
            <NewAssignmentPage />
          </CapabilityGuard>
        )
      },
      {
        path: 'admin/master-data',
        element: (
          <CapabilityGuard capability="assignments.masterData">
            <MasterDataPage />
          </CapabilityGuard>
        )
      },
      { path: ':id', element: <RemountOn param="id"><AssignmentRecordPage /></RemountOn> },
      // The record's tabs are part of the record, so they are the same route
      // with a tab segment rather than pages of their own.
      { path: ':id/:tab', element: <RemountOn param="id"><AssignmentRecordPage /></RemountOn> }
    ]
  }
];
